import {FC} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {
    ConfirmationModalLighterParagraph,
    ConfirmationModalTitle
} from '@components/ui/confirmation-modal-atoms/ConfirmationModalAtoms.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {RadioField} from '@components/commons/radio-field/RadioField.tsx'
import {TreatmentRequestRejectedReasonValidationSchema} from '@/features/treatment-requests/types.ts'
import {ConfirmationDialog} from '@components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms.tsx'
import {DialogStore} from '@ariakit/react'
import {Controller, useForm, FormProvider} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {rejectedReasonToLabel} from '../../utils'
import {ObjectKeys} from '@/utilities/helpers'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'

interface ChangeStatusRejectedModalProps {
    dialogStore: DialogStore
    onConfirm: (rejectFormData: TreatmentRequestRejectedReasonValidationSchema) => void
    isConfirmPending: boolean
    selectedTreatmentRequestsCount: number
}

export const ChangeStatusRejectedModal: FC<ChangeStatusRejectedModalProps> = ({
    dialogStore,
    onConfirm,
    isConfirmPending,
    selectedTreatmentRequestsCount
}) => {
    const {t} = useTranslation()

    const form = useForm<TreatmentRequestRejectedReasonValidationSchema>({
        resolver: zodResolver(TreatmentRequestRejectedReasonValidationSchema)
    })

    const handleConfirm = form.handleSubmit(data => {
        onConfirm(data)
    })

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirm}
                isConfirmPending={isConfirmPending}
                headerSlot={
                    <Flexbox direction="column" gap={2}>
                        <div>
                            <ConfirmationModalLighterParagraph>
                                {t('treatment_requests:change_status_confirm_title')}
                            </ConfirmationModalLighterParagraph>
                            <ConfirmationModalTitle>
                                {t('treatment_requests:status_rejected', {count: 1})}
                            </ConfirmationModalTitle>
                        </div>
                        <ConfirmationModalLighterParagraph>
                            {t('treatment_requests:change_status_confirm_counter', {
                                count: selectedTreatmentRequestsCount
                            })}
                        </ConfirmationModalLighterParagraph>
                    </Flexbox>
                }
                contentSlot={
                    <Flexbox direction="column" gap={2}>
                        <ConfirmationModalLighterParagraph>
                            {t('treatment_requests:select_reason')}
                        </ConfirmationModalLighterParagraph>
                        <Controller
                            control={form.control}
                            name="rejectedReason"
                            render={({field, fieldState}) => (
                                <Flexbox fullWidth direction="column" gap={2}>
                                    {ObjectKeys(rejectedReasonToLabel).map(
                                        reason =>
                                            reason !== 'deceased' && (
                                                <RadioField
                                                    key={reason}
                                                    name={field.name}
                                                    value={reason}
                                                    label={t(rejectedReasonToLabel[reason])}
                                                    checked={field.value == reason}
                                                    onChange={event => field.onChange(event.currentTarget.value)}
                                                />
                                            )
                                    )}
                                    {fieldState.error?.message && <InputHelpText error={t('errors:required')} />}
                                </Flexbox>
                            )}
                        />
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
